// @ts-strict-ignore
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { yupResolver } from '@hookform/resolvers/yup'
import { useQueryClient } from '@tanstack/react-query'
import { useAtom } from 'jotai'
import { useForm, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useKeyPress } from '@node-space/hooks'
import { Box, Button, Callout, Sheet, toast } from '@node-space/ui-components'
import {
  useUpdateBeneficiaryMutationChallengeRequest,
  useUpdateBeneficiaryMutationPreRequest,
} from 'hooks/mutations/useBeneficiariesMutation'
import { reactQueryName } from 'reactQueryKeys/reactQueryKeys'
import {
  BaseErrorResponse,
  BeneficiaryEntityType,
  ChallengeRequest,
  EditBeneficiaryModalStep,
  UpdateBeneficiaryFormPayload,
  UpdateBeneficiaryFormPayloadKeys,
} from 'types/beneficiaries'
import { updateBeneficiarySchema } from '../AddBeneficiary/components/schemas/updateBeneficiarySchema'
import { beneficiaryToUpdateAtom } from './atoms.ts/beneficiaryToUpdateAtom'
import { EditBeneficiaryConfirm } from './components/EditBeneficiaryConfirm'
import { EditBeneficiaryForm } from './components/EditBeneficiaryForm'

interface EditBeneficiarySheetProps {
  isOpen: boolean
  isNestedSheet?: boolean
}

export const EditBeneficiarySheet = ({
  isOpen,
  isNestedSheet = false,
}: EditBeneficiarySheetProps) => {
  const { t } = useTranslation()

  const [beneficiaryToUpdate, setBeneficiaryToUpdate] = useAtom(beneficiaryToUpdateAtom)
  const {
    onClose,
    onBack: onBackHandler,
    beneficiary,
    onUpdateBeneficiary,
  } = beneficiaryToUpdate || {}

  const [step, setStep] = useState<EditBeneficiaryModalStep>(
    EditBeneficiaryModalStep.EDIT_BENEFICIARY
  )

  const sheetHeader = useMemo(() => {
    return step === EditBeneficiaryModalStep.EDIT_BENEFICIARY
      ? t('manageBeneficiaries.editBeneficiary')
      : t('confirmBeneficiaryDetails')
  }, [step])

  const canGoBack = useMemo(() => {
    switch (step) {
      case EditBeneficiaryModalStep.CONFIRMATION:
        return true
      case EditBeneficiaryModalStep.EDIT_BENEFICIARY:
      default:
        return false
    }
  }, [step])

  const handleOnBack = useCallback(() => {
    if (!canGoBack) return

    switch (step) {
      case EditBeneficiaryModalStep.EDIT_BENEFICIARY:
        onClose?.()
        break
      case EditBeneficiaryModalStep.CONFIRMATION:
        setValue('password', '')
        setIsOtpError(false)
        setIsGenericError(false)
        setStep(EditBeneficiaryModalStep.EDIT_BENEFICIARY)
        break
      default:
        onClose?.()
    }
  }, [step, onClose])

  const handleOnClose = () => {
    handleCleanup()
    onClose?.()
  }

  useEffect(() => {
    setBeneficiaryToUpdate({
      ...beneficiaryToUpdate,
      header: sheetHeader,
      onBack: canGoBack ? handleOnBack : undefined,
    })
  }, [step, canGoBack])

  const updateFormSchema = updateBeneficiarySchema(t)
  const { control, formState, register, setValue, trigger, clearErrors, watch, setError } =
    useForm<UpdateBeneficiaryFormPayload>({
      resolver: yupResolver(updateFormSchema),
    })

  const formValues = watch()

  const [alias, password, businessName, firstName, lastName] = useWatch({
    control,
    name: ['alias', 'password', 'businessName', 'firstName', 'lastName'],
    defaultValue: {
      alias: beneficiary?.alias,
      password: '',
      businessName: beneficiary?.businessName || '',
      firstName: beneficiary?.firstName || '',
      lastName: beneficiary?.lastName || '',
    },
  })

  useEffect(() => {
    if (beneficiary) {
      setValue('alias', beneficiary?.alias)

      if (beneficiary.entityType === BeneficiaryEntityType.BUSINESS) {
        setValue('businessName', beneficiary?.businessName || '')
      } else {
        setValue('firstName', beneficiary?.firstName || '')
        setValue('lastName', beneficiary?.lastName || '')
      }
    }
  }, [beneficiary, setValue])

  const queryClient = useQueryClient()
  const { mutate: mutatePreRequest, isPending: isLoadingPreRequest } =
    useUpdateBeneficiaryMutationPreRequest()
  const {
    mutate: mutatePostRequest,
    isPending: isLoadingPostRequest,
    reset: resetPostRequest,
  } = useUpdateBeneficiaryMutationChallengeRequest()
  const [isGenericError, setIsGenericError] = useState(false)
  const [isOtpError, setIsOtpError] = useState<boolean>(false)
  const [updateBeneficiaryState, setUpdateBeneficiaryState] = useState<ChallengeRequest>()

  const isBusiness = useMemo(
    () => beneficiary?.entityType === BeneficiaryEntityType.BUSINESS,
    [beneficiary?.entityType]
  )

  const handleOnchange = (field: UpdateBeneficiaryFormPayloadKeys, value: string) => {
    setValue(field, value)
  }

  const orchestratedPreRequest = () => {
    const beneficiaryNamePayload = isBusiness ? { businessName } : { firstName, lastName }
    const payload = {
      ...beneficiaryToUpdate?.beneficiary,
      alias,
      ...beneficiaryNamePayload,
    }

    delete payload?.transferType
    delete payload?.bvnkAccountReference
    return payload
  }

  const resetForm = () => {
    setValue('password', '')
    setIsOtpError(false)
    setIsGenericError(false)
  }

  const onBeneficiaryUpdatePreRequest = async () => {
    resetForm()

    const fields = [
      'alias',
      ...(beneficiary?.entityType === BeneficiaryEntityType.BUSINESS
        ? ['businessName']
        : ['firstName', 'lastName']),
    ]
    const isValidDetails = await trigger(fields as UpdateBeneficiaryFormPayloadKeys[])

    if (isValidDetails) {
      clearErrors()
      setIsGenericError(false)
      const updateBeneficiaryPayload = orchestratedPreRequest()
      mutatePreRequest(updateBeneficiaryPayload, {
        onError: (error: BaseErrorResponse) => {
          if (error?.status === 428) {
            setUpdateBeneficiaryState({
              ...updateBeneficiaryState,
              challenge: error?.data?.challenge?.value,
              mfaCallbackUrl: error?.data?.callbackUrl,
            })
            setStep(EditBeneficiaryModalStep.CONFIRMATION)
          } else {
            setIsGenericError(true)
          }
        },
      })
    }
  }

  const onBeneficiaryUpdatePostRequest = async () => {
    const isValidDetails = await trigger(['password'])

    if (isValidDetails) {
      clearErrors()
      setIsGenericError(false)
      const otpChallengeRequest = {
        challenge: updateBeneficiaryState?.challenge,
        tokenResponse: formValues?.password,
        mfaCallbackUrl: updateBeneficiaryState?.mfaCallbackUrl,
      }

      mutatePostRequest(otpChallengeRequest, {
        onSuccess: async () => {
          handleCleanup()
          await queryClient.invalidateQueries({ queryKey: [reactQueryName.BENEFICIARIES] })
          setBeneficiaryToUpdate(null)
          onUpdateBeneficiary?.(orchestratedPreRequest())
          toast({
            header: t('manageBeneficiaries.beneficiaryDescriptionUpdated'),
            variant: 'success',
          })
        },
        onError: (error: BaseErrorResponse) => {
          const { data } = error || {}
          const { details, message } = data || {}
          const { errors } = details || {}
          const aliasAlreadyinUseError = 'already in use for this account'
          const isAliasError =
            errors?.requestBody?.[0]?.includes(aliasAlreadyinUseError) ||
            message?.includes(aliasAlreadyinUseError)

          const bvknErrorStatus = error?.status

          if (bvknErrorStatus === 403) {
            setIsOtpError(true)
            setIsGenericError(false)
            setError('password', { message: t('incorrectOtpCode') })
          } else if (isAliasError) {
            setError('alias', { message: t('aliasError') })
            setIsOtpError(false)
            setIsGenericError(false)
            setStep(EditBeneficiaryModalStep.EDIT_BENEFICIARY)
          } else {
            setIsOtpError(false)
            setIsGenericError(true)
          }
        },
        onSettled: () => {
          resetPostRequest()
        },
      })
    }
  }

  const onKeyPressEscape = useCallback(() => {
    onClose()
  }, [onClose])

  useKeyPress('Escape', onKeyPressEscape)

  const onEditSubmit = () => {
    onBeneficiaryUpdatePostRequest()
  }

  const handleCleanup = () => {
    clearErrors()
    setStep(EditBeneficiaryModalStep.EDIT_BENEFICIARY)
    setBeneficiaryToUpdate(null)
    onClose?.()
  }

  const isUpdateEnabled = useMemo(() => {
    if (beneficiaryToUpdate?.isNameUpdated) {
      return true
    }

    const isAliasUpdated = alias && alias !== beneficiary?.alias
    const isBusinessUpdated = businessName && businessName !== beneficiary?.businessName
    const isIndividualUpdated =
      (firstName && firstName !== beneficiary?.firstName) ||
      (lastName && lastName !== beneficiary?.lastName)
    return isAliasUpdated || isBusinessUpdated || isIndividualUpdated
  }, [
    alias,
    beneficiary?.alias,
    businessName,
    firstName,
    lastName,
    beneficiary?.businessName,
    beneficiary?.firstName,
    beneficiary?.lastName,
  ])

  return (
    <Sheet
      header={sheetHeader}
      onBack={onBackHandler}
      isOpen={isOpen}
      onClose={handleOnClose}
      isNestedSheet={isNestedSheet}
    >
      <Box width="full" paddingY={24}>
        {step === EditBeneficiaryModalStep.EDIT_BENEFICIARY && (
          <EditBeneficiaryForm
            isBusiness={isBusiness}
            alias={alias}
            businessName={businessName}
            firstName={firstName}
            lastName={lastName}
            errors={formState?.errors}
            handleOnchange={handleOnchange}
            register={register}
          />
        )}
        {step === EditBeneficiaryModalStep.CONFIRMATION && (
          <EditBeneficiaryConfirm
            beneficiaryUpdatedDetails={formValues}
            isBusiness={isBusiness}
            otpError={isOtpError}
            isLoading={isLoadingPostRequest}
            password={password}
            onEditSubmit={onEditSubmit}
            register={register}
            setValue={setValue}
          />
        )}
        {isGenericError && (
          <Box flex justify="center" paddingB={20}>
            <Callout variant="error">{t('manageBeneficiaries.oopsSomethingWentWrong')}</Callout>
          </Box>
        )}
        {step === EditBeneficiaryModalStep.EDIT_BENEFICIARY && (
          <Box flex justify="between">
            <Button
              onClick={() => {
                handleCleanup()
              }}
              variant="secondary"
              testId="cancel-beneficiary-update"
            >
              {t('cancel')}
            </Button>
            <Button
              testId="update-beneficiary"
              onClick={onBeneficiaryUpdatePreRequest}
              loading={isLoadingPreRequest}
              disabled={!isUpdateEnabled}
            >
              {t('save')}
            </Button>
          </Box>
        )}
      </Box>
    </Sheet>
  )
}
